// „Montant à payer“ of an incoming invoice (user decision 14.10.2026): what will be paid – the invoice total (TTC),
// the amount after escompte (calculated exactly like EscCalc), only the VAT, or a typed amount. Same rules as the
// backend (apps/api/src/compta/import-sessions/rules.ts); the server's value wins after each save.
// EscCalc's own rounding (round2 on euros) is the reference here, so this module uses its numbers; everything else in
// the app keeps BigInt cents.
import { escTotal, PAY_MODE_LABELS, type PayMode } from '@rimon/contracts';
import { toCents, toDecimal } from '../api/money';
import { tr2 } from '../i18n/lang';
import type { DraftFields } from './importSessions';

const isAmount = (v?: string) => !!v && /^-?\d+(\.\d+)?$/.test(v);
const escRate = (f: DraftFields) => (f.escompte?.rate && isAmount(f.escompte.rate) && Number(f.escompte.rate) > 0 && Number(f.escompte.rate) < 100 ? Number(f.escompte.rate) : null);

/** Amount after escompte, per VAT rate as EscCalc (without lines: from the total). */
export function afterEscompte(f: DraftFields): string | undefined {
  const rate = escRate(f);
  if (rate === null) return undefined;
  const lines = f.lines?.length && f.lines.every((l) => isAmount(l.net) && isAmount(l.rate)) ? f.lines : null;
  if (lines) return escTotal(lines.map((l) => ({ net: Number(l.net), rate: Number(l.rate) })), rate).toFixed(2);
  if (!isAmount(f.gross)) return undefined;
  const c = toCents(f.gross!), r = BigInt(Math.round(rate * 100));
  return toDecimal((c * (10000n - r) + 5000n) / 10000n);
}

export const escompteValid = (f: DraftFields, today: string) => escRate(f) !== null && (!!f.escompte?.applyAnyway || !f.escompte?.until || f.escompte.until >= today);
export const defaultMode = (f: DraftFields, today: string): PayMode => (escompteValid(f, today) ? 'ESCOMPTE' : 'TTC');

export function amountFor(f: DraftFields, mode: PayMode, custom?: string): string | undefined {
  if (mode === 'CUSTOM') return custom;
  if (mode === 'ESCOMPTE') return afterEscompte(f);
  if (mode === 'TVA') { const v = f.lines?.filter((l) => isAmount(l.vat)); return v?.length ? toDecimal(v.reduce((s, l) => s + toCents(l.vat!), 0n)) : undefined; }
  return isAmount(f.gross) ? toDecimal(toCents(f.gross!)) : undefined;
}

export const modeLabel = (m: PayMode) => tr2(PAY_MODE_LABELS[m][0], PAY_MODE_LABELS[m][1]);
